import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HandIcon from "../../../../app/icons/HandIcon";
import {
  canShowMenuItem,
  getAuthUser,
  isPortalAdmin,
  isVirtualUser,
} from "../../../../utils/permissions";
import "./MobileGridNavigation.css";
import "./mobile-layout.css";

interface MenuItem {
  moduleKey?: string;
  to?: string;
  icon: React.ReactNode;
  title: string;
  isAdminOnly?: boolean;
  virtualUsersHidden?: boolean;
  children?: Array<{
    to: string;
    title: string;
    icon?: React.ReactNode;
    virtualUsersHidden?: boolean;
  }>;
}

const BiIcon = ({ name }: { name: string }) => (
  <i className={`bi ${name}`} aria-hidden="true" />
);

export const MobileGridNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
  const user = getAuthUser();

  // Keep icons in sync with AsideMenuMain
  const menuItems: MenuItem[] = [
    {
      icon: <BiIcon name="bi-people" />,
      title: "Account Management",
      children: [
        {
          to: "/users/list",
          title: "Dealers & End Users",
          icon: <BiIcon name="bi-people" />,
        },
        {
          to: "/virtual-accounts",
          title: "Team Members",
          icon: <BiIcon name="bi-person-plus" />,
          virtualUsersHidden: true,
        },
      ],
    },
    {
      moduleKey: "devices",
      to: "/devices/list",
      icon: <BiIcon name="bi-cpu" />,
      title: "Devices",
    },
    {
      moduleKey: "monitor",
      to: "/monitors",
      icon: <BiIcon name="bi-display" />,
      title: "Monitor",
    },
    {
      moduleKey: "tracker_pairing",
      to: "/tracker-pairing",
      icon: <BiIcon name="bi-camera" />,
      title: "Pair IMEI - Camera",
    },
    {
      moduleKey: "quick_pair_imeis",
      to: "/quickPairImeis/list",
      icon: <HandIcon />,
      title: "Pair IMEI - Manual Entry",
    },
    {
      moduleKey: "quick_transfers",
      to: "/quickTransfers/list",
      icon: <BiIcon name="bi-arrow-left-right" />,
      title: "Quick Customer Transfer",
    },
    {
      to: "/vin-error-reports",
      icon: <BiIcon name="bi-exclamation-triangle" />,
      title: "VIN Error Reports",
      isAdminOnly: true,
    },
    {
      moduleKey: "sold_reports",
      to: "/sold-reports",
      icon: <BiIcon name="bi-receipt" />,
      title: "Sold Reports",
    },
    {
      title: "KEYper",
      icon: <BiIcon name="bi-key-fill" />,
      isAdminOnly: true,
      children: [
        {
          to: "/keyper/assets",
          title: "Assets",
          icon: <BiIcon name="bi-box-seam" />,
        },
        {
          to: "/keyper/systems",
          title: "Systems",
          icon: <BiIcon name="bi-hdd-rack" />,
        },
      ],
    },
    {
      to: "/welcomeEmailLogs/list",
      icon: <BiIcon name="bi-envelope-open" />,
      title: "Welcome Email Logs",
      isAdminOnly: true,
    },
    {
      to: "/welcomeSmsLogs/list",
      icon: <BiIcon name="bi-chat-dots" />,
      title: "SMS Logs",
      isAdminOnly: true,
    },
    {
      to: "/rolesManagement/list",
      icon: <BiIcon name="bi-shield-lock" />,
      title: "Roles",
      isAdminOnly: true,
    },
  ];

  const filteredItems = menuItems
    .map((item) => {
      if (item.children?.length) {
        const children = item.children.filter((child) => {
          if (child.virtualUsersHidden && isVirtualUser(user)) {
            return false;
          }
          if (child.to.startsWith("/users")) {
            return canShowMenuItem("accounts");
          }
          return true;
        });
        if (!children.length) return null;
        return { ...item, children };
      }
      return item;
    })
    .filter((item): item is MenuItem => {
      if (!item) return false;

      if (item.virtualUsersHidden && isVirtualUser(user)) {
        return false;
      }

      if (item.isAdminOnly && !isPortalAdmin(user)) {
        return false;
      }

      if (item.moduleKey) {
        return canShowMenuItem(item.moduleKey, { adminOnly: item.isAdminOnly });
      }

      return true;
    });

  const isItemActive = (item: MenuItem) => {
    if (item.to) {
      const basePath = item.to.replace(/\/list$/, "");
      if (
        location.pathname === item.to ||
        location.pathname === basePath ||
        location.pathname.startsWith(`${basePath}/`)
      ) {
        return true;
      }
    }

    return (
      item.children?.some((child) => location.pathname.startsWith(child.to)) ??
      false
    );
  };

  useEffect(() => {
    if (location.pathname.startsWith("/keyper")) {
      setExpandedMenu("KEYper");
    }
    if (
      location.pathname.startsWith("/users") ||
      location.pathname.startsWith("/virtual-accounts")
    ) {
      setExpandedMenu("Account Management");
    }
  }, [location.pathname]);

  const handleItemClick = (item: MenuItem) => {
    if (item.children?.length) {
      setExpandedMenu((current) => (current === item.title ? null : item.title));
      return;
    }

    if (item.to) {
      navigate(item.to);
    }
  };

  const expandedItem = filteredItems.find((item) => item.title === expandedMenu);

  return (
    <div className="mobile-grid-navigation">
      <div className="row g-3">
        {filteredItems.map((item) => {
          const isActive = isItemActive(item);
          const isExpanded = expandedMenu === item.title;

          return (
            <div key={item.title} className="col-4">
              <button
                type="button"
                onClick={() => handleItemClick(item)}
                className={`mobile-menu-card w-100 d-flex align-items-center px-2 py-2 ${
                  isActive ? "active" : ""
                } ${item.children?.length ? "mobile-menu-card--has-sub" : ""}`}
              >
                <div className="d-flex align-items-center w-100">
                  <div className="mobile-menu-icon me-2">{item.icon}</div>
                  <div className="mobile-menu-title flex-grow-1">{item.title}</div>
                  {item.children?.length ? (
                    <i
                      className={`bi bi-chevron-${isExpanded ? "up" : "down"} mobile-menu-chevron`}
                    />
                  ) : null}
                </div>
              </button>
            </div>
          );
        })}
      </div>

      {expandedItem?.children?.length ? (
        <div className="mobile-submenu mt-3">
          <div className="mobile-submenu__label">{expandedItem.title}</div>
          <div className="mobile-submenu__items">
            {expandedItem.children.map((child) => {
              const isChildActive = location.pathname.startsWith(child.to);

              return (
                <button
                  key={child.to}
                  type="button"
                  onClick={() => navigate(child.to)}
                  className={`mobile-submenu__item ${isChildActive ? "active" : ""}`}
                >
                  {child.icon ? (
                    <span className="mobile-submenu__icon">{child.icon}</span>
                  ) : null}
                  {child.title}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};
